import { useState } from 'react';
import { useFormik } from 'formik';
import { Eye, EyeOff } from 'lucide-react';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { APP } from '@/config';
import { ERRORS } from '@/constants';
import { ApiError } from '@/services/api/client';
import { useLogin } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { splitFormErrors, toFormErrors } from '@/shared/lib/forms';
import { cn } from '@/shared/lib/utils';
import type { MfaChallenge } from '@/types';
import { FormBanner } from './FormBanner';
import { FIELD, LABEL, SUBMIT } from './styles';

/**
 * Step one of signing in: the password.
 *
 * Worth reading as the pattern for every form in this codebase: Formik owns the
 * field state and submission, Yup states the rules, and a 422 from the server is
 * mapped back onto the fields that caused it.
 *
 * The server validates every request with Zod regardless. Yup here is for
 * immediate feedback, never a decision.
 *
 * A correct password does NOT sign anybody in - every staff account carries a
 * mandatory second factor - so success here means a challenge, handed upward
 * rather than acted on, and the page swaps in the code step.
 */
const schema = Yup.object({
  email: Yup.string()
    .trim()
    .required('Enter your email address.')
    // Deliberately not a stricter pattern. Over-strict email rules reject valid
    // addresses - plus-addressing, new TLDs - and the server has the last word.
    .email('Enter a valid email address.')
    .max(320, 'That address is too long.'),
  password: Yup.string().required('Enter your password.'),
});

export function LoginForm({ onChallenge }: { onChallenge: (challenge: MfaChallenge) => void }) {
  const login = useLogin();

  /*
   * Reveal is local and deliberately never lifted or persisted: a password left
   * legible across a remount is a shoulder-surfing hazard, not a convenience.
   */
  const [revealed, setRevealed] = useState(false);

  const formik = useFormik({
    initialValues: { email: '', password: '' },
    validationSchema: schema,
    onSubmit: (values, helpers) => {
      login.mutate(
        { email: values.email.trim(), password: values.password },
        {
          onSuccess: (result) => {
            /*
             * The response is still typed as a union, so this narrows rather
             * than assumes. In practice the server always demands a second
             * factor here; if that ever changes, this stops compiling rather
             * than silently dropping a completed login on the floor.
             *
             * Narrowed on `challengeId` rather than on `status`, because the
             * two branches share no discriminant property - the token branch
             * has no `status` at all, so reading one would not compile.
             */
            if ('challengeId' in result) onChallenge(result);
          },
          onError: (error) => {
            /*
             * A 422 arrives keyed by request path - `body.email` - so it can be
             * shown against the input that caused it rather than as a banner.
             */
            if (error instanceof ApiError && error.fieldErrors) {
              const { placed } = splitFormErrors(toFormErrors(error.fieldErrors), values);
              helpers.setErrors(placed);
            }
          },
        },
      );
    },
  });

  const apiError = login.error instanceof ApiError ? login.error : null;

  /**
   * A banner for anything that is not about one field.
   *
   * Suppressed when field errors exist, because those are shown in place - but
   * only when at least one of them could be placed. An error with no input to
   * attach it to would otherwise fail completely silently.
   */
  const banner = (): string | null => {
    if (!apiError) return null;

    if (apiError.fieldErrors) {
      const { unplaced } = splitFormErrors(toFormErrors(apiError.fieldErrors), formik.values);
      return unplaced.length > 0 ? apiError.message : null;
    }

    if (apiError.code === 'invalid_credentials') return ERRORS.invalidCredentials;
    if (apiError.code === 'account_locked') return ERRORS.accountLocked;
    if (apiError.code === 'rate_limited') return ERRORS.rateLimited;
    return apiError.message;
  };

  /** Shown once touched, so the form does not scold somebody mid-typing. */
  const error = (field: 'email' | 'password'): string | undefined =>
    formik.touched[field] ? formik.errors[field] : undefined;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      <header>
        <span className="text-[21px] font-semibold tracking-[-0.3px] text-auth-heading">
          Sign in to {APP.name} Admin
        </span>
        <p className="mt-2 mb-6 text-[13px] leading-[1.6] text-muted-foreground">
          Use your staff account. MFA is required on every session.
        </p>
      </header>

      <FormBanner message={banner()} />

      <div className="space-y-1.5">
        <Label htmlFor="email" className={LABEL}>
          Email or username
        </Label>
        <Input
          id="email"
          type="email"
          autoComplete="username"
          autoFocus
          placeholder="you@nudent.ai"
          className={FIELD}
          {...formik.getFieldProps('email')}
          aria-invalid={Boolean(error('email'))}
          aria-describedby={error('email') ? 'email-error' : undefined}
        />
        {error('email') && (
          <p id="email-error" className="text-[10px] text-destructive">
            {error('email')}
          </p>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="flex items-baseline justify-between gap-3">
          <Label htmlFor="password" className={LABEL}>
            Password
          </Label>
          <button
            type="button"
            onClick={() => {
              toast.info(`Staff password resets go through the ${APP.name} platform team.`, {
                description: 'Ask in #nusync-support and an administrator will reset it for you.',
              });
            }}
            className="text-xs font-medium text-brand-500 hover:underline hover:cursor-pointer"
          >
            Forgot?
          </button>
        </div>
        <div className="relative">
          <Input
            id="password"
            type={revealed ? 'text' : 'password'}
            autoComplete="current-password"
            /* Room on the right so a long password never runs under the toggle. */
            className={cn(FIELD, 'pr-11')}
            {...formik.getFieldProps('password')}
            aria-invalid={Boolean(error('password'))}
            aria-describedby={error('password') ? 'password-error' : undefined}
          />
          <button
            type="button"
            /*
             * Skipped in the tab order on purpose - tabbing out of the password
             * should reach Continue, not a display preference. Still reachable
             * and correctly announced for a screen reader via the label below.
             */
            tabIndex={-1}
            onClick={() => setRevealed((shown) => !shown)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            aria-controls="password"
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center rounded-r-[9px] text-[#48536A]/60 transition-colors hover:text-[#48536A] hover:cursor-pointer"
          >
            {revealed ? <EyeOff className="size-4.5" /> : <Eye className="size-4.5" />}
          </button>
        </div>
        {error('password') && (
          <p id="password-error" className="text-[12px] text-destructive">
            {error('password')}
          </p>
        )}
      </div>

      <Button type="submit" className={SUBMIT} disabled={login.isPending}>
        {login.isPending ? 'Checking…' : 'Continue'}
      </Button>

      <p className="mt-4.5 border-t pt-4.5 text-xs leading-[1.6] text-muted-foreground">
        Trouble signing in? Contact the {APP.name} platform team on{' '}
        <span className="font-mono">#nusync-support</span>.
      </p>
    </form>
  );
}
